import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, firstValueFrom, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, switchMap, tap, timeout } from 'rxjs/operators';

import { MaintenanceStatus } from '~shared/types/maintenance.types';
import { MaintenanceRepository } from '~shared/store/maintenance/maintenance.repository';

import { SKIP_AUTH } from '../interceptors/header.interceptor';
import { SKIP_NOTIFICATION } from '../interceptors/error-handler.interceptor';
import { environment } from '../../../environments/environment';

import { WebsocketService } from './websockets.service';
import { AuthService } from './auth.service';

/**
 * Keeps the maintenance store in sync with the backend flag (a dedicated AWS
 * Lambda fronted by SSM Parameter Store) WITHOUT polling:
 *  - one read at startup (APP_INITIALIZER), for fresh loads / the public page;
 *  - a push over the WebSocket when an admin toggles it, for instant effect;
 *  - one read whenever a user signs in, to gate a login during maintenance.
 *
 * Reads skip auth and error notifications: the maintenance page must reach
 * logged-out users and must not toast when the endpoint is unreachable. The act
 * of moving non-admins out (Cognito sign-out + redirect) lives in AppComponent.
 */
@Injectable({ providedIn: 'root' })
export class MaintenanceService {
	private static readonly REQUEST_TIMEOUT_MS = 5_000;

	/** Fail-open default: a broken status endpoint must never lock users out. */
	private static readonly NOT_IN_MAINTENANCE: MaintenanceStatus = {
		enabled: false,
		message: null,
		endsAt: null,
	};

	constructor(
		private readonly http: HttpClient,
		private readonly maintenanceRepository: MaintenanceRepository,
		private readonly websocketService: WebsocketService,
		private readonly authService: AuthService,
	) {}

	/** Resolve the first status before the app renders, then listen for changes. */
	public async init(): Promise<void> {
		const status = await firstValueFrom(this.fetchStatus());
		this.maintenanceRepository.setStatus(status);
		this.listenForChanges();
	}

	public fetchStatus(): Observable<MaintenanceStatus> {
		const context = new HttpContext()
			.set(SKIP_AUTH, true)
			.set(SKIP_NOTIFICATION, true);

		return this.http
			.get<MaintenanceStatus>(`${environment.maintenanceServiceUrl}/status`, { context })
			.pipe(
				timeout(MaintenanceService.REQUEST_TIMEOUT_MS),
				catchError((error) => {
					// Fail open, but loudly — never silently mask a status we couldn't read.
					console.warn('[MaintenanceService] status check failed, assuming online', error);
					return of(MaintenanceService.NOT_IN_MAINTENANCE);
				}),
			);
	}

	/**
	 * Set the maintenance flag (internal ADMIN only — enforced by the backend).
	 * Sent WITH auth (no SKIP_AUTH) so the bearer token is attached and the API
	 * Gateway JWT authorizer + backend role check apply.
	 */
	public setStatus(status: MaintenanceStatus): Observable<MaintenanceStatus> {
		return this.http
			.put<MaintenanceStatus>(`${environment.maintenanceServiceUrl}/status`, status)
			.pipe(tap((updated) => this.maintenanceRepository.setStatus(updated)));
	}

	private listenForChanges(): void {
		// Instant push when an admin toggles maintenance — replaces polling.
		this.websocketService
			.getMaintenanceWSMessages$()
			.subscribe((status) => this.maintenanceRepository.setStatus(status));

		// Re-check once on each sign-in, so a login during maintenance is gated
		// even if the flag changed while the user sat on the (WS-less) login page.
		this.authService.currentUser$
			.pipe(
				filter((user) => !!user),
				distinctUntilChanged((a, b) => a?.email === b?.email),
				switchMap(() => this.fetchStatus()),
			)
			.subscribe((status) => this.maintenanceRepository.setStatus(status));
	}
}
